import { useEffect, useRef } from "react"

import { useDetectGroupTimezoneMutation, useGetGroupSettingsQuery } from "@/entities/group/settings"

import { isIanaTimezone } from "@/shared/lib/timezone"

interface DetectGroupTimezoneContext {
  chatId: number | null
  isInitialized: boolean
  captchaMode: boolean
  shareToken: string | null
}

export const useDetectGroupTimezone = ({
  chatId,
  isInitialized,
  captchaMode,
  shareToken
}: DetectGroupTimezoneContext) => {
  const attemptedChats = useRef(new Set<number>())
  const eligibleContext = isInitialized && chatId !== null && !captchaMode && !shareToken
  const { currentData } = useGetGroupSettingsQuery({ chatId: chatId ?? 0 }, { skip: !eligibleContext })
  const [detectTimezone] = useDetectGroupTimezoneMutation()

  useEffect(() => {
    if (!eligibleContext || !currentData?.allowed_to_change || currentData.settings.timezone_source !== null) {
      return
    }
    if (attemptedChats.current.has(chatId)) return

    const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!deviceTimezone || !isIanaTimezone(deviceTimezone)) return

    attemptedChats.current.add(chatId)
    void detectTimezone({ chatId, timezone: deviceTimezone })
      .unwrap()
      .catch((error: unknown) => {
        console.warn("Failed to detect group timezone:", error)
      })
  }, [chatId, currentData, detectTimezone, eligibleContext])
}
